import { createSelector } from '@reduxjs/toolkit';

import type {
  FilesFileSchemaWithId,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { AssetEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/asset';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { seekableMediaTypes } from '@iconik-io/shared-lib/src/utils/media_type';
import { selectMediaFormats } from '@iconik-io/web/src/panels/asset_files/selectors';

import { EXTRASMALL_SCREEN } from '../../../../constants/breakpoints';
import {
  ACLS_KEY,
  ASSET_METADATA_KEY,
  COMMENTS_KEY,
  FACE_RECOGNITION,
  FILES_KEY,
  HISTORY_KEY,
  POSTERS_KEY,
  PUBLISH_KEY,
  RELATIONS_KEY,
  REVIEWS_KEY,
  SHARES_KEY,
  SUBCLIPS_KEY,
  TIME_BASED_METADATA_KEY,
  TRANSCRIPTIONS_KEY,
} from '../../../../constants/data';
import type { AppAppState } from '../../../../ducks/app/types';
import {
  selectAssetFiles,
  selectAssetOriginalFormats,
  selectAssetProxy,
} from '../../../../ducks/pages/asset/selectors';
import {
  getIsWmPublishPanelEnabled,
  getIsWmPublishSupportsTransferAndPublishEnabled,
} from '../../../../ducks/settings/select_features';
import { selectUploads } from '../../../../ducks/uploader/selectors';
import { selectCurrentVersionId } from '../../../../ducks/version/selectors';
import { selectPersonCountByStatus } from '../../../../panels/asset_face_recognition/selectors';
import { getUploadingBadge } from '../../../../panels/uploads/helpers';
import { hasUploadRoles } from '../../../../panels/uploads/uploader_roles';
import type { AppState } from '../../../../store/types';
import type { PermissionValues } from '../../../../types/common';
import { getPublishDisabledReason } from '../../../../utils/get_publish_disabled_reason';
import { selectUserRoles } from '../../../../utils/get_user_roles';
import {
  assetHasFaceRecognitionSupport,
  assetHasSubclipSupport,
} from '../../../../utils/media_type';
import selectMergedPermissions from '../../../permissions_manager/selectors';
import type { PanelItem } from '../../types';
import {
  getAclsPanelItem,
  getAssetHistoryPanelItem,
  getCommentsPanelItem,
  getFaceRecognitionPanelItem,
  getFilesPanelItem,
  getMetadataPanelItem,
  getPostersPanelItem,
  getPublishPanelItem,
  getRelationsPanelItem,
  getSharesPanelItem,
  getSubclipsPanelItem,
  getTimeBasedMetadataPanelItem,
  getTranscriptionsPanelItem,
  getUploaderPanelItem,
  reviewAndApprovePanelItem,
} from '../common';

const getApp = (state: AppState): AppAppState => state.appReducer;

const getEntityId = (
  _state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
) => entityId;

const getVersionId = (
  _state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  _entityId: string | undefined,
  versionId: string | undefined,
) => versionId;

const getMergedPermissions = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
): PermissionValues | undefined =>
  selectMergedPermissions(state, OBJECT_TYPE_ASSETS, entityId);

const getAsset = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
): AssetEntityNormalized | undefined =>
  entityId ? state.entities.assets[entityId] : undefined;

const getAssetFiles = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
): FilesFileSchemaWithId[] => selectAssetFiles(state, entityId, versionId);

const getAssetProxy = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
): FilesProxySchemaWithId | undefined =>
  selectAssetProxy(state, entityId, versionId);

const getSmallScreen = (state: AppState) =>
  state.displayReducer.breakpoint <= EXTRASMALL_SCREEN;

const getAssetOriginalFormats = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
) => selectAssetOriginalFormats(state, entityId, versionId);

const getAssetMediaFormats = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
) => selectMediaFormats(state, entityId, versionId);

const getPersonCountByStatus = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
) =>
  selectPersonCountByStatus(
    state,
    selectCurrentVersionId(state, entityId, versionId),
  );

export default createSelector(
  getSmallScreen,
  selectUserRoles,
  getApp,
  getMergedPermissions,
  getAsset,
  getAssetProxy,
  getEntityId,
  getVersionId,
  selectUploads,
  getAssetFiles,
  getAssetOriginalFormats,
  getIsWmPublishPanelEnabled,
  getIsWmPublishSupportsTransferAndPublishEnabled,
  getAssetMediaFormats,
  getPersonCountByStatus,
  (
    isSmallScreen,
    userRoles,
    app,
    mergedPermissions,
    asset,
    assetProxy,
    entityId,
    _versionId,
    uploads,
    assetFiles,
    assetOriginalFormats,
    isWmPublishPanelEnabled,
    isWmPublishSupportsTransferAndPublishEnabled,
    mediaFormats,
    personCountByStatus,
  ): PanelItem[] => {
    const canUpload = hasUploadRoles(userRoles);
    const isPlaceholderAssetType = asset?.type === 'PLACEHOLDER';
    const isLinkAssetType = asset?.type === 'LINK';
    const isSubclipAssetType = asset?.type === 'SUBCLIP';
    const isPostAssetType = asset?.type === 'POST';
    if (!mergedPermissions?.read && !isSubclipAssetType) {
      if (canUpload) {
        return [getUploaderPanelItem(userRoles, getUploadingBadge(uploads))];
      }
      return [];
    }

    const proxyMediaType = assetProxy ? getProxyMediaType(assetProxy) : 'file';
    const seekableMediaType = seekableMediaTypes.has(proxyMediaType);
    const publishDisabledReason = getPublishDisabledReason(
      mediaFormats,
      isWmPublishSupportsTransferAndPublishEnabled,
    );
    let publishDisabledTooltip;
    switch (publishDisabledReason) {
      case 'NOT_AUDIO_OR_VIDEO':
        publishDisabledTooltip = 'Publishing supports video and audio assets.';
        break;
      case 'NO_CLOUD_FORMAT':
        publishDisabledTooltip =
          'Publishing requires at least one online media format stored in the cloud.';
        break;
      default:
        publishDisabledTooltip = undefined;
    }

    const { assetDefaultSections } = app;
    const panels = [];

    if (
      userRoles.canReadMetadataCategories &&
      userRoles.canReadMetadataViews &&
      userRoles.canReadMetadataFields &&
      userRoles.canReadMetadataValues
    ) {
      panels.push(
        getMetadataPanelItem(
          OBJECT_TYPE_ASSETS,
          ASSET_METADATA_KEY,
          asset?.category ?? undefined,
          false,
          assetDefaultSections.includes(ASSET_METADATA_KEY),
        ),
      );
    }

    if (userRoles.canReadSegments) {
      panels.push(
        getCommentsPanelItem(assetDefaultSections.includes(COMMENTS_KEY)),
      );

      if (seekableMediaType && userRoles.canReadTranscriptions) {
        panels.push(
          getTranscriptionsPanelItem(
            entityId,
            assetDefaultSections.includes(TRANSCRIPTIONS_KEY),
          ),
        );
      }
    }

    if (userRoles.canReadApprovalRequest && !isPostAssetType) {
      panels.push(
        reviewAndApprovePanelItem(assetDefaultSections.includes(REVIEWS_KEY)),
      );
    }

    const canHaveTimeBasedSegments =
      isPlaceholderAssetType || seekableMediaType;
    if (userRoles.canReadSegments && canHaveTimeBasedSegments) {
      panels.push(
        getTimeBasedMetadataPanelItem(
          assetDefaultSections.includes(TIME_BASED_METADATA_KEY),
        ),
      );
    }

    if (assetHasSubclipSupport(proxyMediaType, asset?.type)) {
      panels.push(
        getSubclipsPanelItem(assetDefaultSections.includes(SUBCLIPS_KEY)),
      );
    }

    const shouldShowFilesPanelForSubclip =
      isSubclipAssetType && assetFiles.length > 0;

    const shouldShowFilesPanel =
      !isPlaceholderAssetType &&
      !isLinkAssetType &&
      !isSubclipAssetType &&
      !isPostAssetType;

    if (
      userRoles.canReadFormats &&
      (shouldShowFilesPanel || shouldShowFilesPanelForSubclip)
    ) {
      panels.push(getFilesPanelItem(assetDefaultSections.includes(FILES_KEY)));
    }

    if (
      assetHasFaceRecognitionSupport(
        proxyMediaType,
        asset?.type,
        assetOriginalFormats,
      ) &&
      userRoles.canReadPerson
    ) {
      panels.push(
        getFaceRecognitionPanelItem(
          'Face Recognition',
          OBJECT_TYPE_ASSETS,
          false,
          assetDefaultSections.includes(FACE_RECOGNITION),
          asset?.has_unconfirmed_persons ?? false,
          personCountByStatus.new > 0,
        ),
      );
    }

    if (userRoles.webEnablePosters) {
      panels.push(
        getPostersPanelItem(
          'Posters',
          OBJECT_TYPE_ASSETS,
          false,
          assetDefaultSections.includes(POSTERS_KEY),
        ),
      );
    }

    if (userRoles.canReadAcls) {
      panels.push(
        getAclsPanelItem(
          ACLS_KEY,
          entityId,
          OBJECT_TYPE_ASSETS,
          false,
          assetDefaultSections.includes(ACLS_KEY),
        ),
      );
    }

    if (userRoles.canReadShares && !isPostAssetType) {
      panels.push(
        getSharesPanelItem({
          key: SHARES_KEY,
          disabled: false,
          active: assetDefaultSections.includes(SHARES_KEY),
        }),
      );
    }
    // TODO: Remove feature flag check when WM publish panel is generally available
    if (isWmPublishPanelEnabled && userRoles.canPublish && !asset?.is_blocked) {
      panels.push(
        getPublishPanelItem({
          key: PUBLISH_KEY,
          disabled: Boolean(publishDisabledTooltip),
          disabledTooltipText: publishDisabledTooltip
            ? publishDisabledTooltip
            : undefined,
        }),
      );
    }

    if (userRoles.canReadAssetsHistory) {
      panels.push(
        getAssetHistoryPanelItem(assetDefaultSections.includes(HISTORY_KEY)),
      );
    }

    if (userRoles.canReadAssetRelations) {
      panels.push(
        getRelationsPanelItem(assetDefaultSections.includes(RELATIONS_KEY)),
      );
    }
    if (
      !isSmallScreen &&
      canUpload &&
      !isSubclipAssetType &&
      !isPostAssetType
    ) {
      panels.push(getUploaderPanelItem(userRoles, getUploadingBadge(uploads)));
    }

    return panels.map((panel) => ({
      ...panel,
      disabled: panel.disabled || !asset,
    }));
  },
);
